import { fromJS } from 'immutable';
import has from 'lodash/has';
import Ajv from 'ajv';
import metaSchema from 'ajv/lib/refs/json-schema-draft-04.json';

import schema from '../schema/index.json';
import medialibItem from '../schema/medialibItem.json';
import draftJsContent from '../schema/draftJsContent.json';
import copyright from '../schema/copyright.json';

const subSchemas = {
  medialibItem,
  draftJsContent,
  copyright,
};

function initAjv() {
  const ajv = new Ajv({ removeAdditional: true });

  ajv.addMetaSchema(metaSchema);

  ajv.addSchema(medialibItem, 'medialibItem');
  ajv.addSchema(draftJsContent, 'draftJsContent');
  ajv.addSchema(copyright, 'copyright');

  return ajv;
}

export function loadSchema() {
  const ajv = initAjv();

  Object.keys(schema.definitions).forEach((definition) => {
    if (schema.definitions[definition]) {
      ajv.addSchema(schema.definitions[definition], definition);
    }
  });

  return ajv;
}

export function loadBlockSchema(blockType) {
  const ajv = initAjv();

  if (!schema.definitions[blockType]) {
    return undefined;
  }

  ajv.addSchema(schema.definitions[blockType], blockType);

  return ajv;
}

export function removeAdditionalBySchema(blocks) {
  const ajv = loadSchema();

  const result = [];

  blocks.forEach((block) => {
    if (block.get('type').substring(0, 1) === '_' || !ajv.getSchema(block.get('type'))) {
      return;
    }

    const cleanBlock = removeUnsupportedInlineStyles(block);
    let plainBlock;

    if (cleanBlock.get('type') === 'QUOTE' && !cleanBlock.get('_withImage') && cleanBlock.get('image')) {
      plainBlock = cleanBlock.delete('image').toJS();
    } else {
      plainBlock = cleanBlock.toJS();
    }

    ajv.validate(block.get('type'), plainBlock);

    result.push(plainBlock);
  });

  return result;
}

export function removeUnsupportedInlineStyles(block) {
  const blockSchema = schema.definitions[block.get('type')];

  if (blockSchema) {
    const draftJsFieldNames = getDraftJSFieldsByType(block.get('type'));
    return block.map((fieldValue, fieldName) => (fieldValue && draftJsFieldNames.includes(fieldName))
      ? fromJS(fieldValue)
        .update('blocks', draftJsBlocks => draftJsBlocks.map(draftJsBlock => draftJsBlock.update('inlineStyleRanges', inlineStyleRanges => inlineStyleRanges.filter(inlineStyle => draftJsContent.properties.blocks.items.properties.inlineStyleRanges.items.properties.style.enum.includes(inlineStyle.get('style'))))))
        .toJS()
      : fieldValue);
  }

  return block;
}

export default function getBlockColorSchemes(blockType) {
  let colorSchemes = [];

  const blockSchema = schema.definitions[blockType];
  if (blockSchema && has(blockSchema, 'properties.colorScheme.enum')) {
    colorSchemes = blockSchema.properties.colorScheme.enum;
  }

  return colorSchemes;
}

export function getMaxLength(definition, property) {
  const blockSchema = schema.definitions[definition];
  let maxLength;
  let properties;

  if (blockSchema) {
    if (blockSchema.properties) {
      properties = blockSchema.properties;
    } else if (blockSchema.allOf) {
      blockSchema.allOf.some((schemaItem) => {
        if (schemaItem.properties) {
          properties = schemaItem.properties;
          return true;
        }
        return false;
      });
    }

    if (properties) {
      const valueField = properties[property];

      if (!valueField) {
        return maxLength;
      }

      if (valueField.type === 'string' && valueField.maxLength) {
        maxLength = valueField.maxLength;
      } else if (valueField.allOf) {
        valueField.allOf.some((item) => {
          const field = (item.$ref && subSchemas[item.$ref])
            ? subSchemas[item.$ref]
            : item;

          if (has(field, 'properties.blocks.items.properties.text.maxLength')) {
            maxLength = field.properties.blocks.items.properties.text.maxLength;
            return true;
          }

          return false;
        });
      }
    }
  }

  return maxLength;
}

export function getDraftJSFieldsByType(type) {
  if (!schema.definitions[type]) return [];

  const props = schema.definitions[type].properties;
  if (!props) return [];

  return Object.keys(props).filter((prop) => {
    const value = props[prop];

    if (value.$ref === 'draftJsContent') {
      return true;
    }

    if (value.allOf) {
      return value.allOf.some(propData => propData.$ref === 'draftJsContent');
    }

    return false;
  });
}
